import { useState, useMemo } from 'react';
import { Search, X, ChevronDown, ChevronRight, Filter, RotateCcw, Save } from 'lucide-react';
import { useFilter } from '../../lib/filter-context';

function Section({ title, children, defaultOpen = true, count }: { title: string; children: React.ReactNode; defaultOpen?: boolean; count?: number }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b" style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-tertiary hover:bg-hover">
        <span className="flex items-center gap-1.5">
          {title}
          {count !== undefined && count > 0 && (
            <span className="px-1 py-0 bg-accent text-white rounded-full text-[8px] font-bold normal-case">{count}</span>
          )}
        </span>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {open && <div className="px-3 pb-2">{children}</div>}
    </div>
  );
}

const DEFAULT_VISIBLE = 5;

function SearchableCheckboxList({
  items, checkedSet, onToggle, placeholder,
}: {
  items: [string, number][];
  checkedSet: Set<string>;
  onToggle: (key: string) => void;
  placeholder: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');

  // Show checked items first, then by count
  const sorted = [...items].sort((a, b) => {
    const aChecked = checkedSet.has(a[0]) ? 0 : 1;
    const bChecked = checkedSet.has(b[0]) ? 0 : 1;
    if (aChecked !== bChecked) return aChecked - bChecked;
    return b[1] - a[1];
  });

  const isSearching = query.length >= 1;
  const filtered = isSearching
    ? sorted.filter(([key]) => key.toLowerCase().includes(query.toLowerCase()))
    : sorted;

  const needsShowMore = !isSearching && filtered.length > DEFAULT_VISIBLE;
  const visible = (!isSearching && !expanded) ? filtered.slice(0, DEFAULT_VISIBLE) : filtered;
  const hiddenCount = filtered.length - DEFAULT_VISIBLE;

  return (
    <>
      {/* Search box -- visible when expanded or many items */}
      {(expanded || isSearching || items.length > DEFAULT_VISIBLE) && expanded && (
        <div className="relative mb-1.5">
          <Search size={10} className="absolute left-2 top-1/2 -translate-y-1/2 text-tertiary" />
          <input type="text" value={query} onChange={e => setQuery(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-6 pr-2 py-0.5 text-[10px] border rounded-sm bg-white focus:outline-none focus:border-accent"
            style={{ borderColor: 'rgba(33,35,39,0.15)' }} />
        </div>
      )}
      <div className={expanded ? 'max-h-48 overflow-auto' : ''}>
        {visible.map(([key, count]) => (
          <label key={key} className="flex items-center gap-2 py-[3px] cursor-pointer hover:bg-hover rounded px-1 -mx-1">
            <input type="checkbox" checked={checkedSet.has(key)} onChange={() => onToggle(key)}
              className="w-3 h-3 rounded border-gray-300 text-accent focus:ring-accent shrink-0" />
            <span className="flex-1 text-[11px] text-primary truncate">{key || '(bos)'}</span>
            <span className="text-[9px] text-tertiary tabular-nums">{count}</span>
          </label>
        ))}
        {visible.length === 0 && isSearching && (
          <div className="text-[10px] text-tertiary py-2 text-center">Sonuc yok</div>
        )}
      </div>
      {needsShowMore && (
        <button onClick={() => { setExpanded(!expanded); if (expanded) setQuery(''); }}
          className="mt-1 px-1 text-[11px] font-medium text-accent hover:underline">
          {expanded ? 'Daha az' : `+${hiddenCount} daha...`}
        </button>
      )}
    </>
  );
}

function RadioItem({ label, value, selected, onChange }: { label: string; value: string; selected: boolean; onChange: () => void }) {
  return (
    <label className="flex items-center gap-2 py-[3px] cursor-pointer hover:bg-hover rounded px-1 -mx-1">
      <input type="radio" checked={selected} onChange={onChange} name={value}
        className="w-3 h-3 border-gray-300 text-accent focus:ring-accent" />
      <span className="flex-1 text-[11px] text-primary">{label}</span>
    </label>
  );
}

// ---- Category Tree Component ----

interface CatNode {
  code: string;
  description: string;
  children: CatNode[];
  leafCodes: string[];  // all leaf (level 2) codes under this node
  itemCount: number;
}

function buildCategoryTree(
  categories: {code: string; description: string; parent_code: string | null}[],
  itemCountBySubCat: Map<string, number>,
): CatNode[] {
  // Find roots (level 0): parent_code is null
  const roots = categories.filter(c => c.parent_code === null);

  function getLeafCodes(code: string): string[] {
    const children = categories.filter(c => c.parent_code === code);
    if (children.length === 0) return [code]; // this is a leaf
    return children.flatMap(c => getLeafCodes(c.code));
  }

  function buildNode(cat: {code: string; description: string; parent_code: string | null}): CatNode {
    const children = categories.filter(c => c.parent_code === cat.code);
    const childNodes = children.map(c => buildNode(c)).sort((a, b) => a.description.localeCompare(b.description));
    const leafCodes = getLeafCodes(cat.code);
    const itemCount = leafCodes.reduce((sum, lc) => sum + (itemCountBySubCat.get(lc) || 0), 0);
    return { code: cat.code, description: cat.description, children: childNodes, leafCodes, itemCount };
  }

  return roots.map(r => buildNode(r)).sort((a, b) => a.description.localeCompare(b.description));
}

function CategoryTreeNode({
  node,
  depth,
  expandedNodes,
  toggleExpand,
  checkedSubCats,
  toggleSubCategory,
  toggleSubCategories,
}: {
  node: CatNode;
  depth: number;
  expandedNodes: Set<string>;
  toggleExpand: (code: string) => void;
  checkedSubCats: Set<string>;
  toggleSubCategory: (code: string) => void;
  toggleSubCategories: (codes: string[]) => void;
}) {
  const isLeaf = node.children.length === 0;
  const isExpanded = expandedNodes.has(node.code);

  if (node.itemCount === 0) return null;

  if (isLeaf) {
    // Level 2 leaf: checkbox
    const isChecked = checkedSubCats.has(node.code);
    return (
      <label
        className="flex items-center gap-2 py-[3px] cursor-pointer hover:bg-hover rounded px-1 -mx-1"
        style={{ paddingLeft: `${depth * 12 + 4}px` }}>
        <input type="checkbox" checked={isChecked} onChange={() => toggleSubCategory(node.code)}
          className="w-3 h-3 rounded border-gray-300 text-accent focus:ring-accent shrink-0" />
        <span className="flex-1 text-[11px] text-primary truncate">{node.description}</span>
        <span className="text-[9px] text-tertiary tabular-nums shrink-0 bg-white pl-1 pr-2">{node.itemCount}</span>
      </label>
    );
  }

  // Collapsible node (level 0 or level 1) — also selectable
  const allChecked = node.leafCodes.length > 0 && node.leafCodes.every(c => checkedSubCats.has(c));
  const someChecked = !allChecked && node.leafCodes.some(c => checkedSubCats.has(c));

  return (
    <div>
      <div
        className="flex items-center gap-1 py-[3px] cursor-pointer hover:bg-hover rounded px-1 -mx-1 select-none"
        style={{ paddingLeft: `${depth * 12 + 4}px` }}>
        <input
          type="checkbox"
          checked={allChecked}
          ref={el => { if (el) el.indeterminate = someChecked; }}
          onChange={() => toggleSubCategories(node.leafCodes)}
          className="w-3 h-3 rounded border-gray-300 text-accent focus:ring-accent shrink-0" />
        <div className="flex-1 flex items-center gap-1 min-w-0" onClick={() => toggleExpand(node.code)}>
          {isExpanded
            ? <ChevronDown size={11} className="text-tertiary shrink-0" />
            : <ChevronRight size={11} className="text-tertiary shrink-0" />}
          <span className={`flex-1 text-[11px] truncate ${depth === 0 ? 'font-semibold text-primary' : 'font-medium text-secondary'}`}>
            {node.description}
          </span>
        </div>
        <span className="text-[9px] text-tertiary tabular-nums shrink-0 bg-white pl-1 pr-2">{node.itemCount}</span>
      </div>
      {isExpanded && node.children.map(child => (
        <CategoryTreeNode
          key={child.code}
          node={child}
          depth={depth + 1}
          expandedNodes={expandedNodes}
          toggleExpand={toggleExpand}
          checkedSubCats={checkedSubCats}
          toggleSubCategory={toggleSubCategory}
          toggleSubCategories={toggleSubCategories}
        />
      ))}
    </div>
  );
}

// ---- Main FilterPanel ----

export function FilterPanel() {
  const {
    filters, allItems, allItemBins, allCategories, filterCounts, isFilterActive, loaded,
    setSearch, toggleBrand, toggleVendor, toggleZone, toggleSubCategory, toggleSubCategories,
    setStatus, setSalesActivity, addSpecificItem, removeSpecificItem, resetFilters,
    filteredItemIds, panelOpen, setPanelOpen,
    savedFilters, saveFilter, loadFilter, deleteSavedFilter,
  } = useFilter();

  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [saveFilterName, setSaveFilterName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [expandedCatNodes, setExpandedCatNodes] = useState<Set<string>>(new Set());

  // Build item count by sub_category_code (using cross-filter counts from context)
  const itemCountBySubCat = useMemo(() => {
    // Count items per sub_category_code from allItems, considering current filters minus subCategories
    const counts = new Map<string, number>();
    for (const item of allItems) {
      if (!item.sub_category_code) continue;
      // Use filterCounts.subCategories which already does cross-filter counting
      const count = filterCounts.subCategories.get(item.sub_category_code);
      if (count !== undefined) counts.set(item.sub_category_code, count);
    }
    return counts;
  }, [allItems, filterCounts.subCategories]);

  // Build the category tree from allCategories
  const categoryTree = useMemo(
    () => buildCategoryTree(allCategories, itemCountBySubCat),
    [allCategories, itemCountBySubCat],
  );

  const toggleCatExpand = (code: string) => {
    setExpandedCatNodes(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  if (!panelOpen) return null;

  // Brand entries
  const brandEntries: [string, number][] = [...filterCounts.brands.entries()];
  // Vendor entries
  const vendorEntries: [string, number][] = [...filterCounts.vendors.entries()];

  // Zone counts: distinct item_ids per zone (across all items, not cross-filtered)
  const zoneCounts = (() => {
    const m = new Map<string, Set<string>>();
    for (const ib of allItemBins) {
      if (!ib.zone_code) continue;
      if (!m.has(ib.zone_code)) m.set(ib.zone_code, new Set());
      m.get(ib.zone_code)!.add(ib.item_id);
    }
    return m;
  })();

  const itemSearchResults = itemSearchQuery.length >= 2
    ? allItems.filter(i =>
        i.item_id.includes(itemSearchQuery) ||
        i.description.toLowerCase().includes(itemSearchQuery.toLowerCase())
      ).slice(0, 10)
    : [];

  return (
    <div className="w-[240px] bg-white border-r flex flex-col shrink-0 overflow-hidden"
      style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-surface shrink-0"
        style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
        <div className="flex items-center gap-1.5">
          <Filter size={13} className="text-accent" />
          <span className="text-[12px] font-semibold text-primary">Filtre</span>
          {isFilterActive && (
            <span className="px-1.5 py-0.5 bg-accent text-white rounded-full text-[9px] font-semibold">
              {filteredItemIds.size}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {isFilterActive && (
            <button onClick={resetFilters} title="Sifirla"
              className="p-1 text-tertiary hover:text-accent rounded-sm hover:bg-hover">
              <RotateCcw size={12} />
            </button>
          )}
          <button onClick={() => setPanelOpen(false)}
            className="p-1 text-tertiary hover:text-primary rounded-sm hover:bg-hover">
            <X size={14} />
          </button>
        </div>
      </div>

      {!loaded ? (
        <div className="flex-1 flex items-center justify-center text-tertiary text-[12px]">Yukleniyor...</div>
      ) : (
        <div className="flex-1 overflow-auto">
          {/* Saved Filters */}
          <div className="px-3 py-2 border-b" style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
            <div className="flex items-center gap-1.5 mb-1">
              <select value="" onChange={e => { if (e.target.value) loadFilter(e.target.value); }}
                className="flex-1 px-2 py-1 text-[11px] border rounded-sm bg-white focus:outline-none focus:border-accent"
                style={{ borderColor: 'rgba(33,35,39,0.15)' }}>
                <option value="">Kayitli filtre sec...</option>
                {savedFilters.map(sf => (
                  <option key={sf.name} value={sf.name}>{sf.name}</option>
                ))}
              </select>
              <button onClick={() => setShowSaveInput(!showSaveInput)} title="Kaydet"
                className="p-1 text-tertiary hover:text-accent rounded-sm hover:bg-hover">
                <Save size={13} />
              </button>
            </div>
            {showSaveInput && (
              <div className="flex items-center gap-1 mb-1">
                <input type="text" value={saveFilterName}
                  onChange={e => setSaveFilterName(e.target.value)}
                  placeholder="Filtre adi..."
                  className="flex-1 px-2 py-1 text-[11px] border rounded-sm focus:outline-none focus:border-accent"
                  style={{ borderColor: 'rgba(33,35,39,0.15)' }}
                  onKeyDown={e => { if (e.key === 'Enter' && saveFilterName) { saveFilter(saveFilterName); setSaveFilterName(''); setShowSaveInput(false); } }} />
                <button onClick={() => { if (saveFilterName) { saveFilter(saveFilterName); setSaveFilterName(''); setShowSaveInput(false); } }}
                  className="px-2 py-1 text-[10px] bg-accent text-white rounded-sm hover:bg-accent/90">Kaydet</button>
              </div>
            )}
            {savedFilters.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {savedFilters.map(sf => (
                  <span key={sf.name} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-surface rounded text-[10px] text-secondary">
                    <span className="cursor-pointer hover:text-accent" onClick={() => loadFilter(sf.name)}>{sf.name}</span>
                    <X size={9} className="cursor-pointer hover:text-red-500" onClick={() => deleteSavedFilter(sf.name)} />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Global Search */}
          <div className="px-3 py-2 border-b" style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-tertiary" />
              <input type="text" value={filters.search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Urun ara..."
                className="w-full pl-7 pr-2 py-1 text-[11px] border rounded-sm bg-white focus:outline-none focus:border-accent"
                style={{ borderColor: 'rgba(33,35,39,0.15)' }} />
            </div>
          </div>

          {/* Specific Items */}
          {filters.specificItems.size > 0 && (
            <div className="px-3 py-2 border-b" style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-tertiary mb-1">Secili Urunler</div>
              <div className="flex flex-wrap gap-1">
                {[...filters.specificItems].map(id => (
                  <span key={id} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-accent-light text-accent rounded-full text-[10px] font-medium">
                    {id}
                    <X size={10} className="cursor-pointer hover:text-red-500" onClick={() => removeSpecificItem(id)} />
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Add Specific Item */}
          <Section title="Urun Sec" defaultOpen={false}>
            <div className="relative mb-1">
              <Search size={10} className="absolute left-2 top-1/2 -translate-y-1/2 text-tertiary" />
              <input type="text" value={itemSearchQuery}
                onChange={e => setItemSearchQuery(e.target.value)}
                placeholder="Urun ID veya isim..."
                className="w-full pl-6 pr-2 py-1 text-[10px] border rounded-sm focus:outline-none focus:border-accent"
                style={{ borderColor: 'rgba(33,35,39,0.15)' }} />
            </div>
            {itemSearchResults.length > 0 && (
              <div className="max-h-36 overflow-auto">
                {itemSearchResults.map(item => (
                  <div key={item.item_id}
                    onClick={() => { addSpecificItem(item.item_id); setItemSearchQuery(''); }}
                    className="flex items-center gap-1.5 px-1 py-1 text-[10px] hover:bg-hover rounded cursor-pointer">
                    <span className="font-mono text-accent w-12 shrink-0">{item.item_id}</span>
                    <span className="flex-1 truncate text-primary">{item.description}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Category Tree (hierarchical) */}
          <Section title="Kategoriler" count={filters.subCategories.size}>
            {categoryTree.length > 0 ? (
              <div className="max-h-64 overflow-y-auto overflow-x-hidden pr-3">
                {categoryTree.map(node => (
                  <CategoryTreeNode
                    key={node.code}
                    node={node}
                    depth={0}
                    expandedNodes={expandedCatNodes}
                    toggleExpand={toggleCatExpand}
                    checkedSubCats={filters.subCategories}
                    toggleSubCategory={toggleSubCategory}
                    toggleSubCategories={toggleSubCategories}
                  />
                ))}
              </div>
            ) : (
              <div className="text-[10px] text-tertiary py-2 text-center">Kategori verisi yok</div>
            )}
          </Section>

          {/* Brand */}
          <Section title="Marka" count={filters.brands.size}>
            <SearchableCheckboxList
              items={brandEntries}
              checkedSet={filters.brands}
              onToggle={toggleBrand}
              placeholder="Marka ara..."
            />
          </Section>

          {/* Vendor */}
          <Section title="Tedarikci" count={filters.vendors.size}>
            <SearchableCheckboxList
              items={vendorEntries}
              checkedSet={filters.vendors}
              onToggle={toggleVendor}
              placeholder="Tedarikci ara..."
            />
          </Section>

          {/* Zone */}
          <Section title="Bolge" count={filters.zones.size}>
            {([
              { code: 'CASHCARRY', label: 'C&C' },
              { code: 'LAGER', label: 'LAGER' },
            ] as const).map(z => (
              <label key={z.code} className="flex items-center gap-2 py-[3px] cursor-pointer hover:bg-hover rounded px-1 -mx-1">
                <input type="checkbox" checked={filters.zones.has(z.code)} onChange={() => toggleZone(z.code)}
                  className="w-3 h-3 rounded border-gray-300 text-accent focus:ring-accent shrink-0" />
                <span className="flex-1 text-[11px] text-primary">{z.label}</span>
                <span className="text-[9px] text-tertiary tabular-nums">{zoneCounts.get(z.code)?.size ?? 0}</span>
              </label>
            ))}
          </Section>

          {/* Status */}
          <Section title="Durum" defaultOpen={false}>
            {(['all', 'assigned', 'unassigned', 'missing'] as const).map(v => (
              <RadioItem key={v} value={`status-${v}`}
                label={{ all: 'Tum', assigned: 'Atanmis', unassigned: 'Atanmamis', missing: 'Eksik' }[v]}
                selected={filters.status === v}
                onChange={() => setStatus(v)} />
            ))}
          </Section>

          {/* Sales Activity */}
          <Section title="Satis Aktivitesi" defaultOpen={false}>
            {(['all', '1m', '3m', '6m', 'inactive'] as const).map(v => (
              <RadioItem key={v} value={`sales-${v}`}
                label={{ all: 'Tum', '1m': 'Son 1 ay', '3m': 'Son 3 ay', '6m': 'Son 6 ay', inactive: '6+ ay satis yok' }[v]}
                selected={filters.salesActivity === v}
                onChange={() => setSalesActivity(v)} />
            ))}
          </Section>
        </div>
      )}
    </div>
  );
}
